import React, { useEffect } from 'react';
import { X, Phone, MessageCircle, MapPin, User, Edit2 } from 'lucide-react';
import { format } from 'date-fns';

// Customer profile overlay. This is the ONE place the basic profile (ID, contact, address) is shown on the
// Customer Detail page: the page body shows only the name (title bar) and live service data.
// Closes on the X, the backdrop and Escape; fits phones through the shared .modal rules.
const Field = ({ label, children }) => (children ? (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
    <div style={{ fontWeight: 600, fontSize: '.88rem', overflowWrap: 'anywhere' }}>{children}</div>
  </div>
) : null);

const Group = ({ icon: Icon, title, cols, children }) => (
  <section style={{ marginBottom: 18 }}>
    <h4 style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px', fontSize: '.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
      <Icon size={14} aria-hidden="true" /> {title}
    </h4>
    <div style={{ display: 'grid', gridTemplateColumns: cols ? `repeat(${cols}, minmax(0, 1fr))` : 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px 16px' }}>{children}</div>
  </section>
);

export default function CustomerInfoOverlay({ customer, hasCatv, hasBroadband, onClose, onEdit }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const a = customer.address || {};
  const wa = String(customer.mobile || '').replace(/\D/g, '').slice(-10);
  // Cable TV: house number + society/area only. Broadband: the full installation address.
  const catvAddr = [customer.catv?.houseNumber, a.area].filter(Boolean).join(', ');
  const bbAddr = [customer.broadband?.houseNumber, a.line1, a.line2, a.landmark, a.area].filter(Boolean).join(', ');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Customer information" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Customer information</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close customer information"><X size={16} /></button>
        </div>
        <div className="modal-body">
          <Group icon={User} title="Account">
            <Field label="Customer ID">{customer.customerId}</Field>
            <Field label="Bill no.">{customer.billingNumber != null ? String(customer.billingNumber) : null}</Field>
            <Field label="Billing name">{customer.billingName && customer.billingName !== customer.name ? customer.billingName : null}</Field>
            <Field label="Services">{[hasCatv && 'Cable TV', hasBroadband && 'Broadband'].filter(Boolean).join(' + ')}</Field>
          </Group>

          <Group icon={Phone} title="Contact" cols={2}>
            <Field label="Customer since">{customer.installationDate ? format(new Date(customer.installationDate), 'dd MMM yyyy') : null}</Field>
            <Field label="Mobile">
              {customer.mobile && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <a href={`tel:${customer.mobile}`} style={{ color: 'var(--primary)' }}>{customer.mobile}</a>
                  {wa.length === 10 && (
                    <a href={`https://wa.me/91${wa}`} target="_blank" rel="noreferrer" title="Open WhatsApp" aria-label="Open WhatsApp (new tab)" style={{ display: 'inline-flex', color: 'var(--primary)' }}>
                      <MessageCircle size={15} aria-hidden="true" />
                    </a>
                  )}
                </span>
              )}
            </Field>
            <Field label="Alternate mobile">{customer.alternateMobile}</Field>
            <Field label="Email">{customer.email}</Field>
          </Group>

          <Group icon={MapPin} title="Address" cols={1}>
            {hasCatv && <Field label="Cable TV address">{catvAddr}</Field>}
            {hasBroadband && <Field label="Broadband installation address">{bbAddr}</Field>}
          </Group>
          <Group icon={MapPin} title="City / State / Pincode" cols={3}>
            <Field label="City">{a.city}</Field>
            <Field label="State">{a.state}</Field>
            <Field label="Pincode">{a.pincode}</Field>
          </Group>
        </div>
        {onEdit && (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { onClose(); onEdit(); }}><Edit2 size={13} /> Edit details</button>
            <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
